import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, AlertTriangle, IndianRupee, PackageCheck, Layers, Activity, FileText, Truck, ClipboardList } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function KPI({ label, value, icon: Icon, accent, tid, sub }) {
  return (
    <div className="kpi-card flex flex-col justify-between h-full" data-testid={tid}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon className={`w-4 h-4 ${accent || "text-zinc-400"}`} />
      </div>
      <div className="mt-4">
        <div className="font-display text-4xl leading-none text-zinc-900">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-2 font-mono">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 overline">Loading dashboard…</div>;

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Control Panel</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Warehouse Overview</h1>
        </div>
        <div className="font-mono text-xs text-zinc-500">{new Date().toLocaleString()}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Stock In Hand" value={data.in_hand} icon={PackageCheck} accent="text-[#16A34A]" tid="kpi-in-hand" sub={`${data.total_batches} active batches`} />
        <KPI label="Total Inward" value={data.total_in} icon={TrendingUp} accent="text-[#16A34A]" tid="kpi-inward" sub="Units received" />
        <KPI label="Total Outward" value={data.total_out} icon={TrendingDown} accent="text-[#D97706]" tid="kpi-outward" sub="Units dispatched" />
        <KPI label="Low Stock Alerts" value={data.low_stock_count} icon={AlertTriangle} accent="text-[#DC2626]" tid="kpi-low-stock" sub="Batches below threshold" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Purchase Value" value={`₹${data.total_purchase_value.toLocaleString()}`} icon={IndianRupee} tid="kpi-purchase-value" />
        <KPI label="Revenue" value={`₹${data.total_revenue.toLocaleString()}`} icon={Activity} accent="text-[#16A34A]" tid="kpi-revenue" />
        <KPI label="Gross Profit" value={`₹${data.gross_profit.toLocaleString()}`} icon={Layers} accent={data.gross_profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"} tid="kpi-profit" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Open Purchase Orders" value={`${data.open_po_count ?? 0}`} icon={FileText} accent="text-[#1769AA]" tid="kpi-open-po" sub={`₹${(data.open_po_value ?? 0).toLocaleString()} committed`} />
        <KPI label="Upcoming Deliveries" value={`${data.upcoming_deliveries_count ?? 0}`} icon={Truck} accent="text-[#D97706]" tid="kpi-upcoming" sub="Next 7 days" />
        <KPI label="Outstanding SO Value" value={`₹${(data.outstanding_so_value ?? 0).toLocaleString()}`} icon={ClipboardList} accent="text-[#28B516]" tid="kpi-outstanding-so" sub={`${data.outstanding_so_count ?? 0} open orders`} />
      </div>

      {data.upcoming_deliveries?.length > 0 && (
        <div className="border border-zinc-200 bg-white rounded-2xl">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-3">
            <Truck className="w-4 h-4 text-[#D97706]" />
            <div className="font-display text-lg uppercase text-zinc-900">Upcoming Deliveries (Next 7 Days)</div>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="upcoming-po-table">
              <thead><tr><th>PO No.</th><th>Vendor</th><th>Expected</th><th className="text-right">Total ₹</th><th>Status</th></tr></thead>
              <tbody>
                {data.upcoming_deliveries.map((p) => (
                  <tr key={p.po_number}>
                    <td className="text-[#28B516] font-mono font-bold">{p.po_number}</td>
                    <td className="font-semibold">{p.vendor_name}</td>
                    <td>{p.expected_date}</td>
                    <td className="text-right font-mono">{Number(p.total || 0).toLocaleString()}</td>
                    <td><span className="badge-warn">{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="border border-zinc-200 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <div className="overline">Movement Trend</div>
            <div className="font-display text-lg uppercase mt-1 text-zinc-900">Last 14 Days · Inward vs Outward</div>
          </div>
          <div className="flex gap-4 text-xs font-mono text-zinc-700">
            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-[#16A34A]" /> Inward</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-[#D97706]" /> Outward</div>
          </div>
        </div>
        <div className="p-6" style={{ height: 300 }}>
          {data.trend?.length ? (
            <ResponsiveContainer>
              <LineChart data={data.trend}>
                <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E4E7", fontFamily: "JetBrains Mono", color: "#0A0A0A" }} />
                <Line type="monotone" dataKey="inward" stroke="#16A34A" strokeWidth={2} dot={{ fill: "#16A34A", r: 3 }} />
                <Line type="monotone" dataKey="outward" stroke="#D97706" strokeWidth={2} dot={{ fill: "#D97706", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-500 overline">No movement data yet</div>
          )}
        </div>
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
          <div className="font-display text-lg uppercase text-zinc-900">Low Stock Batches</div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="low-stock-table">
            <thead>
              <tr>
                <th>Batch #</th>
                <th>Brand / Model</th>
                <th>Type</th>
                <th className="text-right">In Hand</th>
                <th className="text-right">Threshold</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.low_stock_batches?.length === 0 && (
                <tr><td colSpan="6" className="text-center text-zinc-500 py-8">All batches healthy</td></tr>
              )}
              {data.low_stock_batches?.map((b) => (
                <tr key={b.batch_number}>
                  <td className="text-[#16A34A] font-semibold">{b.batch_number}</td>
                  <td className="font-sans">{b.brand} <span className="text-zinc-400">/</span> {b.model}</td>
                  <td className="font-sans">{b.battery_type}</td>
                  <td className="text-right">{b.in_hand}</td>
                  <td className="text-right text-zinc-500">{b.low_stock_threshold}</td>
                  <td><span className="badge-danger">Critical</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
